/**
 * The campaign FAQ — approved copy, 30 September 2026 (Q11).
 *
 * Six answers belong to the route and two are shared by every route, in that
 * order. The approved text is the whole page: Or's note was explicit that
 * there are to be no visible placeholders or internal instructions, and no
 * numbers or limits in the value-proposition answer — so nothing here
 * paraphrases, summarises or adds a caveat.
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { copyFor, isAudience, SHARED, SHARED_FAQ } from './audienceCopy';
import CampaignShell from './CampaignShell';
import MockShell from './mockup/MockShell';
import { MockFaq } from './mockup/MockProse';
import { isMockup, resolveVariant } from './campaignVariant';
import './Campaign.css';

export default function CampaignFaqPage({ onRequestDemo }) {
  const { audience } = useParams();
  const navigate = useNavigate();
  if (!isAudience(audience)) return null;
  // Sticky for the journey — see campaignVariant.
  const mock = isMockup(resolveVariant());
  const copy = copyFor(audience);
  // One form for every entry point (§4). Defaulted here so a page
  // rendered straight from a route still has somewhere to send people.
  const requestDemo = onRequestDemo || (() => navigate(`/c/${audience}/demo`));
  const entries = [...copy.faq, ...SHARED_FAQ];

  return mock ? (
    // Or's FAQ is an accordion on the 850px rail.
    <MockShell audience={audience} rail={850} onRequestDemo={requestDemo}>
      <MockFaq audience={audience} onBack={() => navigate(`/c/${audience}`)} onRequestDemo={requestDemo} />
    </MockShell>
  ) : (

    /* Full width, not the prose column: eight answers in a 768px ribbon down
       the middle of a 1400px page read as a leftover, and the site uses its
       width for exactly this kind of grid. The answers themselves stay at a
       readable measure inside their columns. */
    <CampaignShell audience={audience} showResults onRequestDemo={requestDemo}>
      <p className="cmp-eyebrow cmp-eyebrow--orange">Questions</p>
      <h1 className="cmp-title cmp-title--section">{copy.faqIntro}</h1>

      <div className="cmp-faq cmp-faq--grid">
        {entries.map((entry) => (
          <section className="cmp-faq-entry" key={entry.question}>
            <h2 className="cmp-faq-question">{entry.question}</h2>
            {/* The approved answers carry paragraph breaks; rendering them as
                one block would run the demo invitation into the sentence
                before it. */}
            {entry.answer.split('\n\n').map((para) => (
              <p className="cmp-faq-answer" key={para.slice(0, 40)}>{para}</p>
            ))}
            {entry.demoCta && (
              <div className="cmp-actions">
                <button type="button" className="cmp-btn cmp-btn--secondary" onClick={requestDemo}>
                  {SHARED.requestAGuidedDemo}
                </button>
              </div>
            )}
          </section>
        ))}
      </div>
    </CampaignShell>
  );
}
